import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Users, MessageSquare, QrCode, CheckCircle, 
  ArrowRight, ArrowLeft, Sparkles, Shield, Clock, Image,
  Phone, Mail, Calendar, Star, Camera, Bell
} from "lucide-react";

interface ProviderOnboardingProps {
  isOpen: boolean;
  onComplete: () => void;
  locationName?: string;
}

// Summary slide component for the playbook
const PlaybookSummarySlide = () => (
  <div className="space-y-6">
    <div className="text-center mb-4">
      <h2 className="text-xl sm:text-2xl font-bold mb-2">The TrueTox Provider Playbook</h2>
      <p className="text-sm text-muted-foreground">From Lead to Loyal Member</p>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Member Conversion Funnel */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold text-primary uppercase tracking-wide text-center">
          Member Conversion Funnel
        </h3>
        
        <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 rounded-xl p-3 border border-cyan-500/20">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 text-cyan-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Respond in 2-4 Hours</p>
              <p className="text-xs text-muted-foreground">Use calls, texts, and emails</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 rounded-xl p-3 border border-orange-500/20">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Master the Consultation</p>
              <p className="text-xs text-muted-foreground">Personalized treatment plans</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl p-3 border border-blue-500/20">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Secure Next Visit Day 1</p>
              <p className="text-xs text-muted-foreground">Book 3-4 months out</p>
            </div>
          </div>
        </div>
      </div>

      {/* Member Retention Loop */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold text-primary uppercase tracking-wide text-center">
          Member Retention Loop
        </h3>
        
        <div className="bg-gradient-to-br from-teal-500/10 to-teal-500/5 rounded-xl p-3 border border-teal-500/20">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center flex-shrink-0">
              <QrCode className="w-4 h-4 text-teal-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Use QR Code Check-In</p>
              <p className="text-xs text-muted-foreground">Track visits for rewards</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-xl p-3 border border-amber-500/20">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">3-Month Rebooking Rule</p>
              <p className="text-xs text-muted-foreground">Proactive appointment reminders</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-xl p-3 border border-purple-500/20">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <Camera className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="font-semibold text-sm">Showcase Your Best Work</p>
              <p className="text-xs text-muted-foreground">Upload photos & testimonials</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-xl p-4 text-center border border-primary/20">
      <p className="text-sm font-medium text-foreground">
        💡 Quick tip: Save this playbook in your portal under Help & Resources
      </p>
    </div>
  </div>
);

const slides = [
  {
    icon: Sparkles,
    iconBg: "bg-gradient-to-br from-primary/20 to-primary/5",
    iconColor: "text-primary",
    title: "Welcome to the Provider Portal",
    subtitle: "Your command center for managing leads & members",
    content: "This portal gives you everything you need to track consultation requests, manage Tox Pass members, and grow your practice with True Aesthetics.",
    tips: [],
    customContent: null
  },
  {
    icon: MessageSquare,
    iconBg: "bg-gradient-to-br from-blue-500/20 to-blue-500/5",
    iconColor: "text-blue-500",
    title: "Managing Leads",
    subtitle: "Turn inquiries into loyal patients",
    content: "The Leads tab shows all consultation requests for your location. Stay on top of new inquiries and track your conversion progress.",
    tips: [
      "Respond to new leads within 24 hours for best conversion",
      "Update lead status as you progress through contact",
      "Add notes to keep track of conversations"
    ],
    customContent: null
  },
  {
    icon: QrCode,
    iconBg: "bg-gradient-to-br from-purple-500/20 to-purple-500/5",
    iconColor: "text-purple-500",
    title: "Tox Pass Members",
    subtitle: "Your VIP patient community",
    content: "The Memberships tab shows all Tox Pass holders at your location. Track their journey from activation through treatments.",
    tips: [
      "Check in members when they arrive for appointments",
      "Track visit history to personalize their experience",
      "Use the pipeline view to see where members are in their journey"
    ],
    customContent: null
  },
  {
    icon: Image,
    iconBg: "bg-gradient-to-br from-orange-500/20 to-orange-500/5",
    iconColor: "text-orange-500",
    title: "Showcase Your Work",
    subtitle: "Build trust with real results",
    content: "The Content tab lets you submit before/after photos and testimonials to display on your TrueTox location page.",
    tips: [
      "Add new before/after photos regularly to keep your page fresh",
      "Collect testimonials from happy clients after each visit",
      "High-quality images get better engagement",
      "All submissions are reviewed before going live"
    ],
    customContent: null
  },
  {
    icon: Shield,
    iconBg: "bg-gradient-to-br from-green-500/20 to-green-500/5",
    iconColor: "text-green-500",
    title: "Best Practices",
    subtitle: "Tips for success",
    content: "Follow these guidelines to provide the best experience for your Tox Pass members and maximize conversions.",
    tips: [
      "Verify member identity by asking for their member number",
      "Remind members about their exclusive discounts",
      "Encourage members to book their next appointment before leaving",
      "Keep member notes updated for seamless follow-ups"
    ],
    customContent: null
  },
  {
    icon: null,
    iconBg: "",
    iconColor: "",
    title: "",
    subtitle: "",
    content: "",
    tips: [],
    customContent: PlaybookSummarySlide
  },
  {
    icon: CheckCircle,
    iconBg: "bg-gradient-to-br from-emerald-500/20 to-emerald-500/5",
    iconColor: "text-emerald-500",
    title: "You're All Set!",
    subtitle: "Ready to deliver exceptional care",
    content: "You now have everything you need to manage your leads and members effectively. If you have questions, reach out to orders@trueaesthetics.com.",
    tips: [],
    customContent: null
  }
];

export const ProviderOnboarding = ({ isOpen, onComplete, locationName }: ProviderOnboardingProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const slide = slides[currentSlide];
  const Icon = slide.icon;
  const CustomContent = slide.customContent;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className={`p-0 overflow-hidden ${CustomContent ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`} 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Progress bar */}
        <div className="h-1 bg-muted">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          />
        </div>

        <div className="p-6 sm:p-8 max-h-[80vh] overflow-y-auto">
          {CustomContent ? (
            <CustomContent />
          ) : (
            <>
              {/* Icon */}
              {Icon && (
                <div className={`w-16 h-16 rounded-2xl ${slide.iconBg} flex items-center justify-center mx-auto mb-6`}>
                  <Icon className={`w-8 h-8 ${slide.iconColor}`} />
                </div>
              )}

              {/* Content */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-2">{slide.title}</h2>
                <p className="text-muted-foreground">{slide.subtitle}</p>
                {currentSlide === 0 && locationName && (
                  <p className="text-sm text-primary font-medium mt-2">Managing: {locationName}</p>
                )}
              </div>

              <p className="text-center text-sm text-muted-foreground mb-6">
                {slide.content}
              </p>

              {/* Tips */}
              {slide.tips.length > 0 && (
                <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                  {slide.tips.map((tip, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 text-primary" />
                      </div>
                      <p className="text-sm text-foreground">{tip}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentSlide === 0}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide 
                      ? 'w-6 bg-primary' 
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>

            <Button onClick={handleNext} className="gap-2">
              {currentSlide === slides.length - 1 ? (
                <>
                  Get Started
                  <Sparkles className="w-4 h-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
